/**
 * Brand Logos Section – Infinite Auto-Scroll Marquee
 *
 * How it works:
 *  1. Waits for layout, then measures the pixel width of the original item set.
 *  2. Clones items enough times so the total track is ≥ 2.5× the viewport width
 *     (ensures no empty gaps are ever visible during scrolling).
 *  3. Sets `--brand-logos-shift` CSS property to the negative pixel width of one
 *     original set – the CSS animation moves by exactly this amount then resets.
 *  4. Sets `--brand-logos-duration` so scroll speed is consistent.
 *  5. Pauses on hover / focus (CSS also handles this).
 *  6. Skips animation when `prefers-reduced-motion` is active.
 *  7. Re-initialises cleanly on Shopify section:load (Theme Editor).
 */
(function () {
  'use strict';

  var REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function initBrandLogos(sectionEl) {
    var track = sectionEl.querySelector('.brand-logos-section__track');
    var viewport = sectionEl.querySelector('.brand-logos-section__slider-viewport');
    if (!track || !viewport) return;

    // Honour reduced-motion preference
    if (REDUCED_MOTION) {
      track.style.animation = 'none';
      viewport.style.webkitMaskImage = 'none';
      viewport.style.maskImage = 'none';
      return;
    }

    var originalItems = Array.from(track.children).filter(function (el) {
      return el.getAttribute('aria-hidden') !== 'true';
    });
    if (originalItems.length === 0) return;

    // Speed setting (seconds for one full loop at 5-item count baseline)
    var speedSetting = parseInt(sectionEl.getAttribute('data-scroll-speed'), 10);
    if (isNaN(speedSetting) || speedSetting <= 0) speedSetting = 35;

    // Defer measurement until after the browser has laid out the items
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        setupMarquee(track, viewport, originalItems, speedSetting);
      });
    });
  }

  function setupMarquee(track, viewport, originalItems, speedSetting) {
    // ── Measure original set width ──────────────────────────────────
    var oneSetWidth = 0;
    originalItems.forEach(function (item) {
      oneSetWidth += item.getBoundingClientRect().width;
    });

    // Fallback estimate if layout hasn't settled
    if (oneSetWidth < 1) {
      oneSetWidth = originalItems.length * 240;
    }

    var viewportWidth = viewport.offsetWidth || window.innerWidth;

    // ── Clone items until track is ≥ 2.5× viewport ─────────────────
    // We keep adding "original set" clones until the total scrollable
    // content (track width) comfortably exceeds 2.5× the viewport.
    // The `--brand-logos-shift` animation then moves exactly one original
    // set width per loop, so the reset is always seamless.
    var totalSetsNeeded = Math.ceil((viewportWidth * 2.5) / oneSetWidth);
    totalSetsNeeded = Math.max(totalSetsNeeded, 2); // minimum 2 extra sets

    for (var s = 0; s < totalSetsNeeded; s++) {
      originalItems.forEach(function (item) {
        var clone = item.cloneNode(true);
        clone.setAttribute('aria-hidden', 'true');
        // Make clone links non-focusable (they are decorative duplicates)
        clone.querySelectorAll('a, button, [tabindex]').forEach(function (el) {
          el.setAttribute('tabindex', '-1');
        });
        track.appendChild(clone);
      });
    }

    // ── Set animation translate to exactly one original set width ───
    track.style.setProperty('--brand-logos-shift', '-' + oneSetWidth.toFixed(2) + 'px');

    // ── Scale duration so per-item speed is consistent ───────────────
    // Base speed is calibrated for 5 items. Scale linearly with count.
    var scaledDuration = speedSetting * (originalItems.length / 5);
    scaledDuration = Math.max(8, Math.min(scaledDuration, 120));
    track.style.setProperty('--brand-logos-duration', scaledDuration.toFixed(1) + 's');

    // ── Force animation restart (needed after dynamic DOM changes) ───
    // Remove & re-add animation-name to flush the keyframe in all browsers
    track.style.animationName = 'none';
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        track.style.animationName = '';
      });
    });
  }

  // ── Bootstrap ──────────────────────────────────────────────────────
  function initAll() {
    document.querySelectorAll('.brand-logos-section').forEach(initBrandLogos);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Re-init when a section is reloaded in the Shopify Theme Editor
  document.addEventListener('shopify:section:load', function (event) {
    var sectionEl = event.target.querySelector('.brand-logos-section');
    if (!sectionEl) return;

    // Remove previously made clones before re-initialising
    var track = sectionEl.querySelector('.brand-logos-section__track');
    if (track) {
      Array.from(track.querySelectorAll('[aria-hidden="true"]')).forEach(function (clone) {
        clone.remove();
      });
      // Reset any inline animation overrides
      track.style.animationName = '';
      track.style.removeProperty('--brand-logos-shift');
      track.style.removeProperty('--brand-logos-duration');
    }

    initBrandLogos(sectionEl);
  });
})();
